import { ReactNode, createContext } from 'react'
import { useQuery } from '@tanstack/react-query'
import { tenantActiveOptions } from '$api'
import { BusinessHour, TenantOnboardingStage } from '$api-client/types.gen'
import { useAuth } from '../hooks/use-auth'
import dayjs from 'dayjs'

export type TenancyContext = {
  name?: string,
  tenantId?: string,
  stage?: TenantOnboardingStage,
  onboardedAt?: string | null,
  businessHours: BusinessHour[]
  subscriptionExpiresAt?: string
  contactEmail?: string
  contactPhone?: string
  bookingUrl?: string | null
  isBookingEnabled?: boolean
  isLoading: boolean,
  hasSubscriptionExpired: boolean
}

export const TenancyContext = createContext<TenancyContext>({
  name: undefined,
  tenantId: undefined,
  stage: undefined,
  onboardedAt: undefined,
  businessHours: [],
  subscriptionExpiresAt: undefined,
  contactEmail: undefined,
  contactPhone: undefined,
  bookingUrl: undefined,
  isBookingEnabled: undefined,
  isLoading: true,
  hasSubscriptionExpired: false,
})

export type TenancyProviderProps = {
  children: ReactNode
}

export function TenancyProvider({ children }: TenancyProviderProps) {
  const { user } = useAuth()

  const { data: tenant, isLoading } = useQuery({
    ...tenantActiveOptions(),
    enabled: !!user,
  })

  const context: TenancyContext = {
    name: tenant?.data.name,
    tenantId: tenant?.data.id,
    stage: tenant?.data.stage as TenantOnboardingStage,
    onboardedAt: tenant?.data.onboarded_at,
    businessHours: tenant?.data.business_hours ?? [],
    subscriptionExpiresAt: tenant?.data.subscription_expires_at,
    contactEmail: tenant?.data.contact_email,
    contactPhone: tenant?.data.contact_phone,
    bookingUrl: tenant?.data.booking_url,
    isBookingEnabled: tenant?.data.is_booking_enabled,
    isLoading: isLoading,
    hasSubscriptionExpired: tenant?.data.subscription_expires_at === null || dayjs(tenant?.data.subscription_expires_at).isBefore(dayjs()),
  }

  return (
    <TenancyContext.Provider value={context}>
      { !isLoading && children }
    </TenancyContext.Provider>
  )
}
